import React from 'react';
import { useTranslation } from 'react-i18next';

import Container from 'components/Container';

import {
  Section,
  ReversedDashboardContainer,
  LeftContent,
  SectionTitle,
  SectionDescription,
  FeaturesGrid,
  FeatureCard,
  FeatureIcon,
  FeatureContent,
  FeatureTitle,
  FeatureDescription,
} from './WhatWeDo.styles';

const WhatWeDo = () => {
  const [t] = useTranslation('global');

  const features = [
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" stroke="#F97316" strokeWidth="2" />
          <circle cx="12" cy="12" r="6" stroke="#F97316" strokeWidth="2" />
          <circle cx="12" cy="12" r="2" fill="#F97316" />
          <line
            x1="12"
            y1="2"
            x2="12"
            y2="5"
            stroke="#F97316"
            strokeWidth="2"
          />
          <line
            x1="12"
            y1="19"
            x2="12"
            y2="22"
            stroke="#F97316"
            strokeWidth="2"
          />
          <line
            x1="2"
            y1="12"
            x2="5"
            y2="12"
            stroke="#F97316"
            strokeWidth="2"
          />
          <line
            x1="19"
            y1="12"
            x2="22"
            y2="12"
            stroke="#F97316"
            strokeWidth="2"
          />
        </svg>
      ),
      title: 'Visual Inspection & Defect Detection',
      description:
        'AI-powered vision systems that spot quality issues in real time, reducing manual inspection by 75% and catching defects humans miss.',
      accent: '#F97316',
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            stroke="#3B82F6"
            strokeWidth="2"
          />
          <circle cx="9" cy="10" r="1" fill="#3B82F6" />
          <circle cx="12" cy="10" r="1" fill="#3B82F6" />
          <circle cx="15" cy="10" r="1" fill="#3B82F6" />
        </svg>
      ),
      title: 'Natural Language Data Querying',
      description:
        'Ask complex questions about your business data in plain English and get instant, accurate answers — no SQL or dashboards required.',
      accent: '#3B82F6',
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
            fill="#25d366"
          />
          <path
            d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"
            stroke="#25d366"
            strokeWidth="2"
          />
        </svg>
      ),
      title: 'WhatsApp Business Automation',
      description:
        'Intelligent AI agents that manage your business via WhatsApp — handling inventory, appointments, orders, and customer conversations 24/7.',
      accent: '#25d366',
    },
    {
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
            ry="2"
            stroke="#8B5CF6"
            strokeWidth="2"
          />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#8B5CF6" strokeWidth="2" />
          <circle cx="12" cy="16" r="1.5" fill="#8B5CF6" />
          <line
            x1="12"
            y1="17.5"
            x2="12"
            y2="19"
            stroke="#8B5CF6"
            strokeWidth="2"
          />
        </svg>
      ),
      title: 'Enterprise-Grade Security',
      description:
        'Prompt injection detection, encrypted connections, and isolated client data — your business information stays private and protected.',
      accent: '#8B5CF6',
    },
  ];

  return (
    <Section id="whatWeDo">
      <Container>
        <ReversedDashboardContainer>
          <FeaturesGrid>
            {features.map((feature) => (
              <FeatureCard key={feature.title} $accent={feature.accent}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            ))}
          </FeaturesGrid>
          <LeftContent>
            <SectionTitle>{t('whatWeDo.whatWeDo')}</SectionTitle>
            <SectionDescription>{t('whatWeDo.bring')}</SectionDescription>
          </LeftContent>
        </ReversedDashboardContainer>
      </Container>
    </Section>
  );
};

export default WhatWeDo;
